import { useEffect, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { PlayCircle, StopCircle, Clock, Loader2, Lock } from "lucide-react";
import { toast } from "sonner";

export default function PunchButton() {
  const [status, setStatus] = useState({
    state: "out",
    open_entry: null,
    today_count: 0,
    locked_until_midnight: false,
    last_out_time: null,
    today_hours: 0,
  });
  const [busy, setBusy] = useState(false);
  const [now, setNow] = useState(new Date());

  const load = async () => {
    try {
      const { data } = await api.get("/timesheet/punch/status");
      setStatus(data);
    } catch (_) {}
  };

  useEffect(() => {
    load();
    const tick = setInterval(() => setNow(new Date()), 30_000);
    // Riallinea esattamente a mezzanotte Rome: ricarica lo stato per sbloccare il bottone
    const scheduleMidnight = () => {
      const n = new Date();
      const next = new Date(n);
      next.setHours(24, 0, 10, 0); // 00:00:10 del giorno dopo
      const ms = next.getTime() - n.getTime();
      return setTimeout(() => {
        load();
        setNow(new Date());
        scheduleMidnight();
      }, Math.max(1000, ms));
    };
    const midnightTimer = scheduleMidnight();
    return () => {
      clearInterval(tick);
      clearTimeout(midnightTimer);
    };
  }, []);

  const punch = async () => {
    if (status.locked_until_midnight) return;
    setBusy(true);
    try {
      const { data } = await api.post("/timesheet/punch");
      toast.success(
        data.action === "in"
          ? "⏱️ Timbrato ingresso"
          : `⏱️ Timbrato uscita — ${data.entry.hours.toFixed(1)}h`
      );
      await load();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setBusy(false);
    }
  };

  const isIn = status.state === "in";
  const locked = status.locked_until_midnight;
  const open = status.open_entry;
  const romeMin = (d) => {
    const [h, m] = d.toLocaleTimeString("it-IT", { timeZone: "Europe/Rome", hour12: false }).split(":").map(Number);
    return (h % 24) * 60 + m;
  };
  const fmt = (min) => `${Math.floor(min / 60)}h ${String(Math.floor(min % 60)).padStart(2, "0")}m`;
  const elapsed = open
    ? (() => {
        const [h, m] = (open.start_time || "00:00").split(":").map(Number);
        let diff = romeMin(now) - (h * 60 + m);
        if (diff < 0) diff += 24 * 60;
        return fmt(diff);
      })()
    : null;

  // Countdown fino a mezzanotte (ora di Roma) quando bloccato
  const untilMidnight = locked ? fmt(24 * 60 - romeMin(now)) : null;

  const borderColor = locked ? "#64748b" : isIn ? "#059669" : "#0284c7";
  const bgClass = locked
    ? "bg-gradient-to-br from-slate-500 to-slate-700"
    : isIn
    ? "bg-gradient-to-br from-emerald-500 to-emerald-700"
    : "bg-gradient-to-br from-sky-500 to-sky-700";

  return (
    <section
      className="rounded-2xl border-2 overflow-hidden shadow-lg"
      style={{ borderColor }}
      data-testid="punch-widget"
    >
      <button
        type="button"
        onClick={punch}
        disabled={busy || locked}
        data-testid="punch-button"
        className={`w-full p-6 sm:p-8 text-left transition ${bgClass} text-white ${
          locked ? "cursor-not-allowed opacity-95" : "active:scale-[0.98]"
        }`}
      >
        <div className="flex items-center gap-4">
          <div className="w-16 h-16 sm:w-20 sm:h-20 rounded-full bg-white/20 border-4 border-white/30 flex items-center justify-center shrink-0">
            {busy ? (
              <Loader2 className="w-8 h-8 animate-spin" />
            ) : locked ? (
              <Lock className="w-10 h-10" />
            ) : isIn ? (
              <StopCircle className="w-10 h-10" />
            ) : (
              <PlayCircle className="w-10 h-10" />
            )}
          </div>
          <div className="flex-1 min-w-0">
            <div className="text-xs sm:text-sm uppercase tracking-widest font-bold opacity-90">
              {locked ? "Giornata chiusa" : isIn ? "Al lavoro" : "Fuori servizio"}
            </div>
            <div className="font-display text-2xl sm:text-4xl font-extrabold leading-tight mt-1">
              {locked ? "Si riattiva alle 00:00" : isIn ? "Timbra Uscita" : "Timbra Entrata"}
            </div>
            {isIn && open && !locked && (
              <div
                className="text-xs sm:text-sm opacity-90 mt-2 flex items-center gap-2 font-mono"
                data-testid="punch-elapsed"
              >
                <Clock className="w-3.5 h-3.5 shrink-0" /> <span className="whitespace-nowrap">In turno dalle <b>{open.start_time}</b></span> · <span className="whitespace-nowrap">{elapsed}</span>
              </div>
            )}
            {locked && (
              <div
                className="text-xs sm:text-sm opacity-90 mt-2 flex items-center gap-2 font-mono flex-wrap"
                data-testid="punch-locked-info"
              >
                <Clock className="w-3.5 h-3.5" />
                Uscita firmata alle <b>{status.last_out_time}</b> · oggi{" "}
                <b>{Number(status.today_hours || 0).toFixed(1)}h</b>
                {untilMidnight && (
                  <>
                    {" "}
                    · riattivazione tra <b>{untilMidnight}</b>
                  </>
                )}
              </div>
            )}
          </div>
        </div>
      </button>
    </section>
  );
}
